import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import Messages from "../../../constants/Messages";
import { useAuth } from "../../../context/AuthProvider";

import EditStock from "./EditStock";
import StockIn from "./StockIn";
import StockOut from "./StockOut";
import StockTrack from "./StockTrack";
import ReactPaginate from "react-paginate";
import { usePermission } from "../../../context/PermissionContext";
import useKeyboardTableNavigation from "../../../constants/useKeyboardTableNavigation";
import { useContextApi } from "../../../context/ContextApi";
import Table from "../../../constants/Table";
import BarcodeGenerator from "../product/BarCode";
import uRLs from "../../../utils/baseUtils";
import { toPrimaryQuantity, toSecondaryQuantity } from "@heartinz/finance";
import ProductSearchList from "../../../constants/ProductSearchList";
import { secondaryQuanity } from "../../../utils/ProductHandleChange";
import { formatCurrencyFun } from "../../../utils/formatCurrencyFunc";
import { AddIcon, BarcodeIcon, DeleteIcon, RefreshIcon, SearchIcon, XIcon } from "../../../constants/Icons";

const ListStock = ({ isActive }) => {
  const navigate = useNavigate();
  const [isPreLoading, setPreLoading] = useState(true);
  const [isStockData, setStockData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const { getColor } = useContextApi();
  const [isSearchTerm, setSearchTerm] = useState("");
  const [ProductSuggestions, setProductSuggestions] = useState([]);
  const [selectedProductCode, setSelectedProductCode] = useState("");
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [selectedBarcode, setSelectedBarcode] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const ProductSuggestionsRef = useRef(null);
  const suggestionRefs = useRef([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 10;

  const [isBtnLoading, setBtnLoading] = useState(false);
  const [isStockEditModalOpen, setStockEditModalOpen] = useState(false);
  const [isStockEditData, setStockEditData] = useState("");

  const { checkNetwork } = useNetwork();
  const { isUser } = useAuth();
  const showAlert = useShowAlert();
  const { permission, hasPermission, globelPopupOpen } = usePermission();
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);

  const handleSearch = async (term) => {
    if (hasPermission("STOCK", "PRODUCT:search") || permission === undefined) {
      setSearchTerm(term);
      setHasSearched(true);

      if (!term.trim()) {
        setProductSuggestions([]);
        await handleList(0, "");
        setCurrentPage(0);
        return;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      debounceTimerRef.current = setTimeout(async () => {
        try {
          if (!checkNetwork()) return;
          setSuggestionLoading(true);
          controllerRef.current = new AbortController();
          const currentId = ++requestIdRef.current;
          const response = await AxiosInstance.get(
            `module/product/search-hybrid-active?searchtxt=${term}`,
            { signal: controllerRef.current.signal },
          );
          if (currentId === requestIdRef.current) {
            setProductSuggestions(response.data.results || []);
          }
        } catch (error) {
          if (error.name !== "CanceledError" && error.name !== "AbortError") {
            handleError(error, showAlert);
          }
        } finally {
          setSuggestionLoading(false);
        }
      }, 200);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleSearchFind = () => {
    const currentInputValue = searchInputRef.current?.value || "";
    setSearchTerm(currentInputValue);
    setSelectedProductCode(currentInputValue);
    if (!currentInputValue.trim()) {
      handleList(0, "");
    } else {
      handleList(0, currentInputValue);
    }
    setProductSuggestions([]);
    setHasSearched(false);
    setCurrentPage(0);
  };

  const handleSelectProduct = (product) => {
    const code = product.code;
    // setSearchTerm(code);
    setSelectedProductCode(code);
    setProductSuggestions([]);
    setHasSearched(false);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    handleList(0, code);
    setCurrentPage(0);
  };

  const handleOutsideClick = (e) => {
    if (
      ProductSuggestionsRef.current &&
      !ProductSuggestionsRef.current.contains(e.target)
    ) {
      setProductSuggestions([]);
      setHasSearched(false);
      setSearchTerm("");
    }
  };

  const handleList = async (page = 0, term = "") => {
    setPreLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/stock?pageno=${page + 1}&limit=${perPage}&searchtxt=${term}`,
      );
      const results = response.data.results || [];
      setStockData(results);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPreLoading(false);
    }
  };

  const handleClickBarcode = (code) => {
    if (
      hasPermission("PRODUCT", "PRODUCT:viewByCode") ||
      permission === undefined
    ) {
      setSelectedBarcode(code); // set the selected barcode
      setShowBarcodeModal(true); // show the modal
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };
  // const handleSearch = (e) => {
  //   const query = e.target.value.toLowerCase();
  //   setSearchQuery(query);
  //   const filtered =Array.isArray(isStockData)
  //   ?  isStockData.filter(
  //     (item) =>
  //       item.productid.title?.toLowerCase().includes(query) ||
  //       item.productid.code?.toLowerCase().includes(query)
  //   ): [];
  //   setFilteredData(filtered);
  // };

  const handleLink = () => {
    if (hasPermission("STOCK", "STOCK:add") || permission === undefined) {
      const navigatelist = "/liststock";
      navigate("/stock", { state: { navigatelist } });
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const openEditModal = (cItem, cIndex) => {
    if (hasPermission("STOCK", "STOCK:update") || permission === undefined) {
      const isValidDate = (date) => date && !isNaN(new Date(date).getTime());
      setStockEditData({
        ...cItem,
        mrp: CurrencyConverter.toCurrency(cItem?.mrp, "INR"),
        saleprice: CurrencyConverter.toCurrency(cItem?.saleprice, "INR"),
        discount: CurrencyConverter.toCurrency(cItem?.discount, "INR"),
        baseprice: CurrencyConverter.toCurrency(cItem?.baseprice, "INR"),
        taxamount: CurrencyConverter.toCurrency(cItem?.taxamount, "INR"),
        landingcost: CurrencyConverter.toCurrency(
          cItem?.landingcost || 0,
          "INR",
        ),
        manufacturedate: isValidDate(cItem?.manufacturedate)
          ? DateConverter.ISOToDisplayFormat(
              DateConverter.toISO(
                formatDate(new Date(cItem?.manufacturedate)),
                "yyyy-MM-dd",
                isUser.timezone,
              ),
            ).date
          : "",
        expiredate: isValidDate(cItem?.expiredate)
          ? DateConverter.ISOToDisplayFormat(
              DateConverter.toISO(
                formatDate(new Date(cItem?.expiredate)),
                "yyyy-MM-dd",
                isUser.timezone,
              ),
            ).date
          : "",
        cIndex: cIndex,
      });
      setStockEditModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const closeEditModal = () => {
    setStockEditModalOpen(false);
    setStockEditData("");
  };

  const primaryQuanity=(quanity,unitData)=>{
    const qty=toPrimaryQuantity(quanity, unitData);
    return qty?.primaryQty;
  }

  const handleUpdate = async (updatedData) => {

    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        productid: updatedData.productid,
        // stockid: updatedData._id,
        ...(updatedData?.manufacturedate && {
          manufacturedate: DateConverter.convertToTimeZone(
            updatedData?.manufacturedate,
            isUser.timezone,
          ),
        }),
        ...(updatedData?.expiredate && {
          expiredate: DateConverter.convertToTimeZone(
            updatedData?.expiredate,
            isUser.timezone,
          ),
        }),
        mrp: CurrencyConverter.toSubunit(updatedData.mrp, "INR"),
        saleprice: CurrencyConverter.toSubunit(updatedData.saleprice, "INR"),
        taxpercentage: updatedData.taxpercentage,
        // discount: CurrencyConverter.toSubunit(updatedData.discount, "INR"),
        baseprice: CurrencyConverter.toSubunit(updatedData.baseprice, "INR"),
        taxamount: CurrencyConverter.toSubunit(updatedData.taxamount, "INR"),
        isLock: updatedData.isLock,
        landingcost: CurrencyConverter.toSubunit(
          updatedData.landingcost || 0,
          "INR",
        ),
      };
      console.log(updatedData)
      const response = await AxiosInstance.patch(
        `module/stock/${updatedData._id}`,
        data,
      );
      var index = updatedData.cIndex;
      const updatedDatas = response.data.results;

      setStockData((prevStock) =>
        prevStock.map((item) =>
          item.productid === updatedDatas.productid
            ? { ...item, ...updatedDatas, stockid: updatedData?.stockid }
            : item,
        ),
      );
      const stockValue = updatedData?.isSecondaryUnit
        ? primaryQuanity(updatedData?.stock||0, updatedData?.unitConfig)
        : updatedData?.stock||0;
      if (stockValue > 0.0) {
        if (updatedData.stockLeft < stockValue) {
          await handleStockInUpdate(updatedData);
        } else if (updatedData.stockLeft >= stockValue) {
          if (updatedData.stockLeft >= stockValue) {
            await handleStockTrashUpdate(updatedData);
          } else {
            console.log("insufficient Stock");
          }
        } else {
          console.log("euql");
        }
      }
      // setFilteredData((prevFiltered) => {
      //   if (prevFiltered[index]?._id === updatedData._id) {
      //     const updatedFiltered = [...prevFiltered];
      //     updatedFiltered[index] = { ...prevFiltered[index], ...updatedData };
      //     return updatedFiltered;
      //   }
      //   return prevFiltered.map((item) =>
      //     item._id === updatedData._id ? { ...item, ...updatedData } : item
      //   );
      // });
      showAlert("success", Messages.SUCCESS.STOCK_UPDATED);
      closeEditModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const [isStockInModalOpen, setStockInModalOpen] = useState(false);
  const [isStockInData, setStockInData] = useState("");

  const openStockInModal = (cItem, index) => {
    if (
      hasPermission("STOCK", "STOCK:updateStockin") ||
      permission === undefined
    ) {
      setStockInModalOpen(true);
      cItem.cIndex = index;
      setStockInData(cItem);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const closeStockInModal = () => {
    setStockInModalOpen(false);
    setStockInData("");
  };

  const handleStockInUpdate = async (updatedData, quantity) => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        productid: updatedData?.productid,
        // stockid: updatedData._id,
        stocklinkid: updatedData?.stockLinkId,
        quantity: Number(updatedData?.isSecondaryUnit ?
          ((primaryQuanity(updatedData?.stock,updatedData?.unitConfig) - updatedData?.stockLeft ).toFixed(3)):
          (updatedData?.stock - updatedData?.stockLeft).toFixed(3),
        ),
        // quantity: quantity,
      };
      const response = await AxiosInstance.patch(
        `module/stock/stockin/${updatedData?._id}`,
        // `module/stock/stockin/${updatedData?.stockid}`,
        data,
      );
      var index = updatedData.cIndex;
      updatedData = response.data.results;

      // setStockData((prevStock) => {
      //   if (prevStock[index]?._id === updatedData._id) {
      //     const updatedStock = [...prevStock];
      //     updatedStock[index] = { ...prevStock[index], ...updatedData };
      //     return updatedStock;
      //   }
      //   return prevStock.map((item) =>
      //     item._id === updatedData._id ? { ...item, ...updatedData } : item
      //   );
      // });

      setStockData((prevStock) =>
        prevStock.map((item) =>
          item.productid === updatedData.productid
            ? // item?._id === updatedData?._id
              {
                ...item,
                stockIn: updatedData.stockIn,
                stockLeft: updatedData.stockLeft,
              }
            : item,
        ),
      );
      showAlert("success", Messages.SUCCESS.STOCK_UPDATED);
      closeStockInModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const handleStockIn = async (updatedData, quantity) => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        productid: updatedData?.productid,
        // stockid: updatedData._id,
        stocklinkid: updatedData?.stockLinkId,
        quantity: quantity,
      };
      const response = await AxiosInstance.patch(
        `module/stock/stockin/${updatedData?._id}`,
        data,
      );
      var index = updatedData.cIndex;
      updatedData = response.data.results;

      // setStockData((prevStock) => {
      //   if (prevStock[index]?._id === updatedData._id) {
      //     const updatedStock = [...prevStock];
      //     updatedStock[index] = { ...prevStock[index], ...updatedData };
      //     return updatedStock;
      //   }
      //   return prevStock.map((item) =>
      //     item._id === updatedData._id ? { ...item, ...updatedData } : item
      //   );
      // });

      setStockData((prevStock) =>
        prevStock.map((item) =>
          item.productid === updatedData.productid
            ? {
                ...item,
                stockIn: updatedData.stockIn,
                stockLeft: updatedData.stockLeft,
              }
            : item,
        ),
      );
      showAlert("success", Messages.SUCCESS.STOCK_UPDATED);
      closeStockInModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const [isStockOutModalOpen, setStockOutModalOpen] = useState(false);
  const [isStockOutData, setStockOutData] = useState("");

  const openDeleteTrashModal = (cItem, index) => {
    if (
      hasPermission("STOCK", "STOCK:deleteTrash") ||
      permission === undefined
    ) {
      setStockOutModalOpen(true);
      cItem.cIndex = index;
      setStockOutData(cItem);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const closeStockOutModal = () => {
    setStockOutModalOpen(false);
    setStockOutData("");
  };

  const handleStockTrashUpdate = async (updatedData, quantity, remarks) => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        productid: updatedData?.productid,
        stockid: updatedData._id,
        // ...(remarks && { remarks }),
        remarks: "Stock Trashed",
        quantity: Number(updatedData?.isSecondaryUnit ?
          ((updatedData.stockLeft - primaryQuanity(updatedData?.stock,updatedData?.unitConfig)).toFixed(3)):
          (updatedData.stockLeft - updatedData?.stock).toFixed(3),
        ),
      };
      const response = await AxiosInstance.delete(
        `module/stock/trash/${updatedData.stockLinkId}`,
        { data },
      );
      var index = updatedData.cIndex;
      updatedData = response.data.results;

      setStockData((prevStock) =>
        prevStock.map((item) =>
          // item.productid === updatedData.productid
          item?.stockLinkId === updatedData?.stockLinkId
            ? {
                ...item,
                stockLeft: Number(
                  (item.stockLeft - updatedData.quantity).toFixed(3),
                ),
              }
            : item,
        ),
      );

      // setFilteredData((prevFiltered) => {
      //   if (prevFiltered[index]?._id === updatedData._id) {
      //     const updatedFiltered = [...prevFiltered];
      //     updatedFiltered[index] = { ...prevFiltered[index], ...updatedData };
      //     return updatedFiltered;
      //   }
      //   return prevFiltered.map((item) =>
      //     item._id === updatedData._id ? { ...item, ...updatedData } : item
      //   );
      // });
      showAlert("success", Messages.SUCCESS.STOCK_UPDATED);
      closeStockOutModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const handleStockTrash = async (updatedData, quantity, remarks,selectedUnit) => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        productid: updatedData.productid,
        stockid: updatedData._id,
        // stocklid: updatedData.stocklid,
        ...(remarks && { remarks }),
        quantity: Number(
          updatedData?.isSecondaryUnit &&
          selectedUnit === updatedData?.unitConfig?.secondaryUnitCode
            ? primaryQuanity(quantity, updatedData?.unitConfig)
            : quantity
        ),
      };
      const response = await AxiosInstance.delete(
        `module/stock/trash/${updatedData.stockLinkId}`,
        { data },
      );
      var index = updatedData.cIndex;
      updatedData = response.data.results;

      setStockData((prevStock) =>
        prevStock.map((item) =>
          item.productid === updatedData.productid
            ? {
                ...item,
                stockLeft: Number(
                  (item.stockLeft - updatedData.quantity).toFixed(3),
                ),
              }
            : item,
        ),
      );

      // setFilteredData((prevFiltered) => {
      //   if (prevFiltered[index]?._id === updatedData._id) {
      //     const updatedFiltered = [...prevFiltered];
      //     updatedFiltered[index] = { ...prevFiltered[index], ...updatedData };
      //     return updatedFiltered;
      //   }
      //   return prevFiltered.map((item) =>
      //     item._id === updatedData._id ? { ...item, ...updatedData } : item
      //   );
      // });
      showAlert("success", Messages.SUCCESS.STOCK_TRASHED);
      closeStockOutModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const [isTrackViewModal, setTrackViewModal] = useState(false);
  const [isTrackViewData, setTrackViewData] = useState("");

  const handleStockTrack = async (cItem) => {
    setTrackViewModal(true);
    setTrackViewData(cItem);
  };

  const closeTrackModal = () => {
    setTrackViewModal(false);
    setTrackViewData("");
  };

 

  const handleSuggestionNavigation = (e) => {
    if (e.key === "Enter") {
      if (activeIndex >= 0 && ProductSuggestions[activeIndex]) {
        e.preventDefault();
        handleSelectProduct(ProductSuggestions[activeIndex]);
      } else {
        e.preventDefault();
        handleSearchFind();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < ProductSuggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : ProductSuggestions.length - 1,
      );
    }
  };

  useKeyboardTableNavigation({
    isActive,
    containerRef,
    searchTerms: isSearchTerm,
    suggestions: ProductSuggestions,
    modals: {
      isStockEditModalOpen,
      isStockOutModalOpen,
      isStockInModalOpen,
      globelPopupOpen,
    },
    onEnter: openEditModal,
  });

  useEffect(() => {
    if (!isActive) return;

    // small timeout ensures DOM is visible after tab switch
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive]);

  useEffect(() => {
    if (activeIndex >= 0 && suggestionRefs.current[activeIndex]) {
      suggestionRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    handleList(currentPage, isSearchTerm);
  }, [currentPage]);

  const handleRefresh = async () => {
    await handleList(currentPage, isSearchTerm);
  };

  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="page-content">
            <div className="page-breadcrumb d-flex align-items-center mb-3 gap-2">
              <div className="pb-1">
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1 shadow-none"
                  onClick={handleRefresh}
                  disabled={isPreLoading}
                  title="Refresh list"
                >
                  <RefreshIcon iClass={`${isPreLoading ? "bx-spin" : ""}`}/>             
                </button>
              </div>
              <div className="breadcrumb-title pe-3">List Stock </div>
              {/* <div className="ms-auto">
                <button
                  type="button"
                  className="btn btn-primary mt-2 mt-lg-0"
                  onClick={handleLink}
                >
                  <i className="ng ng-ng-add-icon"></i> Add Stock Batch
                </button>
              </div> */}
            </div>

            <div className="row">
              <div className="col">
                <div className="card radius-10 mb-0">
                  <div className="card-body px-0">
                    <div className="row px-3">
                      <div className="col-lg-5 col-12">
                        <div className="ms-auto position-relative">
                          <div
                            className="input-group"
                            ref={ProductSuggestionsRef}
                          >
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search product by code, title, etc..."
                              ref={searchInputRef}
                              value={selectedProductCode}
                              onChange={(e) => {
                                handleSearch(e.target.value);
                                setSelectedProductCode(e.target.value);
                                setActiveIndex(-1);
                              }}
                              onKeyDown={handleSuggestionNavigation}
                              autoComplete="off"
                              autoFocus
                            />
                            {selectedProductCode ? (
                              <span
                                className="input-group-text"
                                onClick={() => {
                                  setSelectedProductCode("");
                                  handleList();
                                }}
                              >
                                <XIcon/>
                              </span>
                            ) : (
                              <button
                                className="input-group-text"
                                onClick={handleSearchFind}
                              >
                                <SearchIcon/>
                              </button>
                            )}
                          </div>
                          {hasSearched &&
                            isSearchTerm &&
                            !suggestionLoading &&
                            ProductSuggestions.length === 0 && (
                              <ul className="suggestions-list">
                                <li className="suggestion-item text-muted">
                                  No products data was found
                                </li>
                              </ul>
                            )}

                          {ProductSuggestions.length > 0 && (
                            <ProductSearchList suggest={ProductSuggestions}
                            suggestionRefs={suggestionRefs}
                             handleSelect={handleSelectProduct} activeIndex={activeIndex}
                             />
                          )}
                        </div>
                      </div>
                      <div className="col-lg-2  col-6 d-flex align-items-center">
                        <div>
                          <strong>Total Stocks:</strong>{" "}
                          <span className="badge btn-label-teal fs-15 ">
                            {totalItems || 0}
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-5 col-6 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-teal mt-2 mt-lg-0"
                          onClick={handleLink}
                        >
                          <AddIcon/> New Stock
                        </button>
                      </div>
                    </div>

                    <div ref={containerRef}>
                      <Table
                        loading={isPreLoading}
                        trClass={true}
                        data={isStockData}
                        onRowClick={(item, index) => openEditModal(item, index)}
                        columns={[
                          {
                            label: "S.No",
                            render: (_, index) => isStockData.length - index,
                          },
                          {
                            label: "SKU Code",
                            render: (item) => (
                              <span className="badge rounded-pill bg-label-navy">
                                {item.productid ? item.code : ""}
                              </span>
                            ),
                          },
                          {
                            label: "Title",
                            render: (item) => (
                              <span className="text-heading fw-medium text-wrap text-left">
                                {item.productid ? item.title : ""}
                              </span>
                            ),
                          },
                          {
                            label: "MRP",
                            render: (item) => (
                              <span className="text-right">
                                {" "}
                                {formatCurrencyFun(item?.mrp)}
                              </span>
                            ),
                          },
                          {
                            label: "Sale Price",
                            tdBodyclass:"p-3",
                            render: (item) => (
                              <span className="text-right">
                                {" "}
                               {formatCurrencyFun(item?.saleprice)}
                              </span>
                            ),
                          },
                          {
                            label: "Tax(%)",
                            render: (item) => (
                              <span className="badge bg-label-peace">
                                {item.taxpercentage
                                  ? `${item.taxpercentage}%`
                                  : "0%"}
                              </span>
                            ),
                          },
                          {
                            label: "LandingCost",
                            render: (item) => (
                              <span className="text-right">
                                {formatCurrencyFun(item?.landingcost)}
                              </span>
                            ),
                          },
                          {
                            label: "Expire",
                            render: (item) =>
                              item.expiredate && (
                                <span className="badge bg-label-wood">
                                  {
                                    DateConverter.ISOToDisplayFormat(
                                      DateConverter.convertToTimeZone(
                                        item.expiredate,
                                        isUser?.timezone,
                                      ),
                                    ).date
                                  }
                                </span>
                              ),
                          },
                          // {
                          //   label: "Unit",
                          //   render: (item) => (
                          //     <span className="badge btn-unit">
                          //       {item?.stockInUnit==="unit"?"": item?.stockInUnit}
                          //     </span>
                          //   ),
                          // },
                          {
                            label: "Stock In",
                            render: (item) => (
                              <span className="badge bg-label-spring">
                               {Number(item?.isSecondaryUnit
                                ? secondaryQuanity(item.stockIn,item?.unitConfig)
                                : item?.stockIn)}{" "} 
                                <span className="font-11 ms-1">
                                {
                                (() => {
                                  const unitCode = item?.isSecondaryUnit
                                    ? item?.unitConfig?.secondaryUnitCode
                                    : item?.unitConfig?.primaryUnitCode;

                                  // return unitCode !== "unit" ? unitCode : "";
                                  return unitCode ;
                                })()
                              }</span>
                              </span>
                            ),
                          },
                          {
                            label: "Stock Left",
                            thClass:"w-15",
                            render: (item,index) => (
                              <div className="no-print position-relative">
                          <div className=" d-flex justify-content-center align-items-center">
                              <span className="stock-status-badge badge bg-label-space">
                                {Number(item?.isSecondaryUnit
                                ? secondaryQuanity(item.stockLeft,item?.unitConfig)
                                : item?.stockLeft)}
                                <span className="font-11 ms-1">
                                {
                                (() => {
                                  const unitCode = item?.isSecondaryUnit
                                    ? item?.unitConfig?.secondaryUnitCode
                                    : item?.unitConfig?.primaryUnitCode;

                                  // return unitCode !== "unit" ? unitCode : "";
                                  return unitCode ;
                                })()
                              }
                                </span>
                              </span>
                              <div className="order-actions order-actions3 opacity-0 opacity-md-100">
                                <button
                                  type="button"
                                  className="btn-label-primary shadow-none me-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClickBarcode(item);
                                  }}
                                >
                                  <BarcodeIcon/>
                                </button>
                                <button
                                  type="button"
                                  className="btn-label-danger shadow-none"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteTrashModal(item, index);
                                  }}
                                >
                                  <DeleteIcon/>
                                </button>
                              </div>
                              </div>
                              </div>
                            ),
                          },
                          // {
                          //   label: "Actions",
                          //   render: (item, index) => (
                          //     <div className="order-actions opacity-0 opacity-md-100">
                          //       <button
                          //         type="button"
                          //         className="btn-label-primary shadow-none me-2"
                          //         onClick={(e) => {
                          //           e.stopPropagation();
                          //           handleClickBarcode(item);
                          //         }}
                          //       >
                          //         <i className="bx bx-barcode"></i>
                          //       </button>
                          //       {/* <button
                          //           type="button"
                          //           className="btn btn-sm btn-label-info shadow-none me-2"
                          //           onClick={() =>
                          //             handleStockTrack(item, index)
                          //           }
                          //         >
                          //           <i className="bx bx-show"></i>
                          //         </button> */}
                          //       {/* <button
                          //           type="button"
                          //           className="btn btn-sm btn-label-teal  shadow-none me-2"
                          //           onClick={(e) =>{
                          //             e.stopPropagation(); openEditModal(item, index)}}
                          //         >
                          //           <i className="ng ng-ng-edit-icon"></i>
                          //         </button>

                          //         <button
                          //           type="button"
                          //           className="btn btn-sm btn-label-teal  shadow-none me-2 "
                          //           onClick={(e) =>{
                          //             e.stopPropagation();
                          //             openStockInModal(item, index)
                          //           }}
                          //         >
                          //           <i className="ng ng-ng-add-icon"></i> Stock
                          //         </button> */}

                          //       <button
                          //         type="button"
                          //         className="btn-label-danger shadow-none"
                          //         onClick={(e) => {
                          //           e.stopPropagation();
                          //           openDeleteTrashModal(item, index);
                          //         }}
                          //       >
                          //         <i className="ng ng-ng-delete-icon"></i>
                          //       </button>
                          //     </div>
                          //   ),
                          // },
                        ]}
                      />
                    </div>
                    {totalPages > 0 && (
                      <div className="d-flex justify-content-center mt-3">
                        <ReactPaginate
                          previousLabel={"← Previous"}
                          nextLabel={"Next →"}
                          breakLabel={"..."}
                          onPageChange={({ selected }) => {
                            setPreLoading(true);
                            setCurrentPage(selected);
                          }}
                          pageRangeDisplayed={5}
                          pageCount={totalPages}
                          containerClassName="pagination"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isStockEditModalOpen && (
        <EditStock
          isActive={isActive}
          editData={isStockEditData}
          onClose={closeEditModal}
          onEdit={handleUpdate}
          isBtnLoading={isBtnLoading}
        />
      )}

      {isStockInModalOpen && (
        <StockIn
          data={isStockInData}
          onClose={closeStockInModal}
          onEdit={handleStockIn}
          isBtnLoading={isBtnLoading}
        />
      )}

      {isStockOutModalOpen && (
        <StockOut
          data={isStockOutData}
          onClose={closeStockOutModal}
          onEdit={handleStockTrash}
          isBtnLoading={isBtnLoading}
        />
      )}

      {isTrackViewModal && (
        <StockTrack isData={isTrackViewData} onClose={closeTrackModal} />
      )}
      {showBarcodeModal && uRLs.BARCODE_PRINT === "true" && (
        <BarcodeGenerator
          barcodeValue={selectedBarcode.code}
          product={selectedBarcode}
          onClose={() => setShowBarcodeModal(false)}
        />
      )}
    </>
  );
};

export default ListStock;
